<template>
    <div class="py-4 container-fluid" style="min-height: 80vh;">
        <div class="row h-100">
            <div class="col-lg-12">
                <div class="mb-4 card" style="min-height: calc(80vh - 2rem);">
                    <div class="p-3 card-body">
                        <div class="d-flex flex-row-reverse justify-content-between">
                            <div class="col-md-12">
                                <div class="numbers">
                                    <p class="mb-3 text-lg text-uppercase font-weight-bold">
                                        Easy Find
                                    </p>
                                    <div class="row">
                                        <div class="col-md-12">
                                            <label for="example-text-input" class="form-control-label">Input</label>
                                            <argon-input type="text" v-model:value="query"
                                                @keyup.enter="handleSearch" />
                                        </div>
                                        <!-- Loading indicator -->
                                        <div class="col-lg-12 mt-4" v-if="isLoading">
                                            <div class="d-flex justify-content-center align-items-center p-4">
                                                <div class="spinner-border text-primary" role="status">
                                                    <span class="sr-only">Memuat...</span>
                                                </div>
                                                <span class="ml-3">Mencari...</span>
                                            </div>
                                        </div>
                                        
                                        <div class="col-lg-12 mt-4" v-else-if="result"
                                            style="overflow-wrap: break-word; word-break: break-word;">
                                            <div v-if="result.success">
                                                <!-- Chart visualization -->
                                                <div v-if="result.type === 'chart'" class="card" style="min-height: 300px;">
                                                    <div class="p-3 pb-0 card-header">
                                                        <div class="d-flex align-items-center">
                                                            <h6 class="mb-0">{{ result.title || 'Chart' }}</h6>
                                                        </div>
                                                    </div>
                                                    <div class="p-3 card-body">
                                                        <div class="row">
                                                            <div class="text-center col-12" :style="{ height: result.chartType === 'doughnut' ? '200px' : '300px' }">
                                                                <div class="chart">
                                                                    <canvas id="easyfind-chart" class="chart-canvas" :height="result.chartType === 'doughnut' ? 200 : 300"></canvas>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <!-- Chart legend -->
                                                        <div class="row mt-3" v-if="result.labels && result.data">
                                                            <div :class="result.chartType === 'doughnut' ? 'col-md-6' : 'col-md-3'" class="mb-2" v-for="(item, index) in result.data" :key="index">
                                                                <div class="row">
                                                                    <div class="col-md-9">
                                                                        <div class="px-2 py-0 d-flex">
                                                                            <span class="badge me-2" :style="{ backgroundColor: getChartColor(index) }">&nbsp;</span>
                                                                            <div class="d-flex flex-column justify-content-center">
                                                                                <h6 class="mb-0 text-sm">{{ result.labels[index] }}</h6>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div class="col-md-3">
                                                                        <div class="d-flex flex-column align-items-end">
                                                                            <h6 class="mb-0 text-sm">{{ item }}</h6>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Explanation if available -->
                                                <div v-else-if="result.explanation"
                                                    class="mt-4 p-3 bg-light rounded text-block"
                                                    style="word-wrap: break-word; word-break: break-word; overflow-wrap: break-word; white-space: pre-wrap;">
                                                    <p class="mb-0"
                                                        style="word-wrap: break-word; word-break: break-word;"
                                                        v-html="formatExplanation(result.explanation)"></p>
                                                </div>

                                                <!-- Table data with improved overflow handling -->
                                                <div v-else-if="result.headers && result.rows" class="table-responsive">
                                                    <table class="table align-items-center mb-0 table-bordered">
                                                        <thead>
                                                            <tr>
                                                                <th v-for="header in result.headers" :key="header"
                                                                    class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                                                                    style="word-wrap: break-word; word-break: break-word; overflow-wrap: break-word; max-width: 200px; white-space: normal;">
                                                                    {{ header }}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(row, index) in result.rows" :key="index">
                                                                <td v-for="header in result.headers" :key="header"
                                                                    style="word-wrap: break-word; word-break: break-word; overflow-wrap: break-word; max-width: 200px; white-space: normal; vertical-align: top;">
                                                                    {{ row[header] }}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <!-- Simple message response -->
                                                <div v-else-if="result.message" class="bg-light text-block p-3 rounded"
                                                    style="word-wrap: break-word; word-break: break-word; overflow-wrap: break-word; white-space: pre-wrap;">
                                                    {{ result.message }}
                                                </div>
                                            </div>

                                            <!-- Error message -->
                                            <div v-else class="alert alert-danger text-block" role="alert"
                                                style="word-wrap: break-word; word-break: break-word; overflow-wrap: break-word; white-space: pre-wrap;">
                                                {{ result.message }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <auth-check></auth-check>
    </div>
</template>
<script>
import ArgonInput from "@/components/ArgonInput.vue";
import AuthCheck from "@/components/authCheck.vue";
import axios from "axios";
import Chart from "chart.js/auto";

export default {
    name: "dashboard-default",
    data() {
        return {
            query: "",
            result: null,
            isLoading: false,
            chartInstance: null,
        };
    },
    methods: {
        async handleSearch() {
            try {
                if (!this.query.trim()) return;

                this.isLoading = true;
                this.result = null;

                const response = await axios.post(process.env.VUE_APP_API_URL + 'chat', {
                    message: this.query
                });
                this.result = response.data;
                
                // Render chart if result type is chart
                if (this.result && this.result.type === 'chart') {
                    // Add a small delay to ensure DOM is fully updated
                    setTimeout(() => {
                        this.renderChart();
                    }, 100);
                }
            } catch (error) {
                console.error('Search error:', error);
                this.result = {
                    success: false,
                    message: error.response?.data?.message || 'Error occurred while searching'
                };
            } finally {
                this.isLoading = false;
            }
        },
        formatExplanation(text) {
            if (!text) return '';
            // Convert **text** to <strong>text</strong>
            return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        },
        getChartColor(index) {
            const colors = [
                '#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c',
                '#ffd600', '#ff9f43', '#ea5455', '#7367f0', '#28c76f'
            ];
            return colors[index % colors.length];
        },
        renderChart() {
            if (!this.result || this.result.type !== 'chart') return;
            
            this.$nextTick(() => {
                const canvas = document.getElementById('easyfind-chart');
                if (!canvas) {
                    console.error('Canvas element not found');
                    return;
                }
                
                // Destroy existing chart if it exists
                if (this.chartInstance) {
                    this.chartInstance.destroy();
                    this.chartInstance = null;
                }
                
                const ctx = canvas.getContext('2d');
                const chartType = this.result.chartType || 'bar';
                const colors = this.result.data.map((_, index) => this.getChartColor(index));
                
                const chartConfig = {
                    type: chartType,
                    data: {
                        labels: this.result.labels,
                        datasets: [{
                            label: this.result.title || 'Data',
                            data: this.result.data,
                            backgroundColor: colors,
                            borderWidth: chartType === 'doughnut' ? 2 : 0,
                            fill: true
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: false
                            }
                        },
                        interaction: {
                            intersect: false,
                            mode: 'index'
                        }
                    }
                };
                
                // Add specific options for doughnut charts
                if (chartType === 'doughnut') {
                    chartConfig.data.datasets[0].cutout = 50;
                    chartConfig.data.datasets[0].weight = 9;
                    chartConfig.data.datasets[0].tension = 1;
                    chartConfig.data.datasets[0].pointRadius = 2;
                }
                
                // Add scales for bar charts
                if (chartType === 'bar') {
                    chartConfig.options.scales = {
                        y: {
                            grid: {
                                drawBorder: false,
                                display: false,
                                drawOnChartArea: false,
                                drawTicks: false
                            },
                            ticks: {
                                display: false
                            }
                        },
                        x: {
                            grid: {
                                drawBorder: false,
                                display: false,
                                drawOnChartArea: false,
                                drawTicks: false
                            },
                            ticks: {
                                display: false
                            }
                        }
                    };
                }
                
                try {
                    this.chartInstance = new Chart(ctx, chartConfig);
                    console.log('Chart created successfully');
                } catch (error) {
                    console.error('Error creating chart:', error);
                }
            });
        }
    },
    beforeUnmount() {
        // Clean up chart instance when component is destroyed
        if (this.chartInstance) {
            this.chartInstance.destroy();
            this.chartInstance = null;
        }
    },
    components: {
        ArgonInput,
        AuthCheck,
    },
};
</script>